import { presenceName, presenceStyleIndex, type PresencePeer } from '@cdf/file-core/browser';

export { presenceInitials, presenceName, presenceSummary, type PresencePeer } from '@cdf/file-core/browser';

/**
 * Live co-editing presence, ported from Solution Studio's collab-presence helpers.
 * Identity, naming and summaries come from the shared package; the tones below are this app's design tokens.
 * Solution Studio picks a hue per person; this monochrome design instead varies tone and line style,
 * so collaborators stay distinguishable without colour. Names come from the server, never the client.
 */
export type PresenceTone = { avatar: string; caret: string; label: string; selection: string };

export const PRESENCE_TONES: readonly PresenceTone[] = [
  { avatar: 'bg-foreground text-background', caret: 'border-foreground border-solid', label: 'bg-foreground text-background', selection: 'bg-foreground/10' },
  { avatar: 'bg-muted-foreground text-background', caret: 'border-muted-foreground border-solid', label: 'bg-muted-foreground text-background', selection: 'bg-muted-foreground/15' },
  { avatar: 'border border-foreground bg-background text-foreground', caret: 'border-foreground border-dashed', label: 'border border-foreground bg-background text-foreground', selection: 'bg-foreground/5 underline decoration-dashed' },
  { avatar: 'border border-dashed border-foreground bg-muted text-foreground', caret: 'border-foreground border-dotted', label: 'border border-dashed border-foreground bg-muted text-foreground', selection: 'bg-muted underline decoration-dotted' },
  { avatar: 'border border-muted-foreground bg-background text-muted-foreground', caret: 'border-muted-foreground border-dashed', label: 'border border-muted-foreground bg-background text-muted-foreground', selection: 'bg-muted-foreground/10 underline decoration-dashed' },
  { avatar: 'bg-muted text-foreground ring-1 ring-foreground', caret: 'border-muted-foreground border-dotted', label: 'bg-muted text-foreground ring-1 ring-foreground', selection: 'bg-muted-foreground/10 underline decoration-dotted' },
];

/** Stable tone for one person across sessions, like Solution Studio's presenceColorForId. */
export function presenceToneForId(id: string): PresenceTone {
  return PRESENCE_TONES[presenceStyleIndex(id, PRESENCE_TONES.length)]!;
}

/**
 * Caret and name label for a remote collaborator. Built with DOM APIs (no HTML strings)
 * because ProseMirror widgets render outside React; the name is untrusted display text.
 */
export function renderPresenceCursor(peer: PresencePeer): HTMLElement {
  const tone = presenceToneForId(peer.actorId);
  const name = presenceName(peer);
  const caret = document.createElement('span');
  caret.className = `relative -ml-px border-l-2 pointer-events-none ${tone.caret}`;
  caret.setAttribute('data-peer-cursor', peer.sessionId);
  caret.setAttribute('aria-label', `${name} cursor`);
  const label = document.createElement('span');
  label.className = `absolute bottom-full left-0 z-10 whitespace-nowrap rounded px-1 text-[10px] leading-4 font-medium ${tone.label}`;
  label.textContent = name;
  caret.appendChild(label);
  return caret;
}
